import { useQuery } from '@tanstack/react-query';
import { searchProducts } from '../services/api';
import { getImageUrl } from '../lib/utils';

export const useSearchQuery = (query, enabled = true) => {
  // Ensure enabled is always a boolean
  const isEnabled = Boolean(enabled && query?.trim() && query.trim().length >= 2);
  
  return useQuery({
    queryKey: ['search', query],
    queryFn: async () => {
      if (!query?.trim()) return { results: [], totalResults: 0, totalPages: 1 };
      const response = await searchProducts(query.trim(), 1, 20);

      // Format the search results
      const products = (response.data.products || []).map(product => ({
        id: product._id,
        name: product.name,
        image: product.images && product.images.length > 0 ? getImageUrl(product.images[0]) : null,
        path: `/product/${product.slug}`,
        category: 'products',
        description: product.shortDescription || '',
        price: product.regularPrice || product.price,
        tags: product.searchTags ? product.searchTags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
      }));

      // Sort results by relevance - exact matches first
      const lowerQuery = query.toLowerCase();
      const sortedResults = products.sort((a, b) => {
        // Exact matches come first
        const aExact = a.name.toLowerCase() === lowerQuery;
        const bExact = b.name.toLowerCase() === lowerQuery;

        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        // Then sort by whether name starts with the query
        const aStarts = a.name.toLowerCase().startsWith(lowerQuery);
        const bStarts = b.name.toLowerCase().startsWith(lowerQuery);

        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        // Check for tags matching
        const aTagMatch = a.tags?.some(tag => tag.toLowerCase().includes(lowerQuery));
        const bTagMatch = b.tags?.some(tag => tag.toLowerCase().includes(lowerQuery));

        if (aTagMatch && !bTagMatch) return -1;
        if (!aTagMatch && bTagMatch) return 1;

        return 0;
      });

      return {
        results: sortedResults,
        totalResults: response.data.totalResults || sortedResults.length,
        totalPages: response.data.totalPages || 1,
      };
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};
